import TransactionActions from "./purchaseComponents/TransactionActions";
import formatPercent from "../../hooks/formatPercent";
import { Box, CircularProgress, Modal } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import moment from "moment";
import { useMutation } from "@tanstack/react-query";
import { LuDownload, LuPrinter } from "react-icons/lu";
import { FaCheckCircle, FaChevronLeft, FaCircle } from "react-icons/fa";
import { RxCross1 } from "react-icons/rx";

import {
  fetchTransactionDetailNewPbf,
  getPrintFakturPbf,
  getSpPrintPbf,
} from "../../../services/purchaseApi";
import { SPECIAL_LABEL, STATUS_TRANSACTION } from "../../constants/constant";
import ErrorPopup from "../../common/particles.jsx/ErrorPopup";
import BreadcrumbPurchase from "./purchaseComponents/BreadcrumbPurchase";
import PurchaseHeader from "./purchaseComponents/PurchaseHeader";

const toIDR = (val) =>
  val?.toLocaleString("id-ID", {
    style: "currency",
    currency: "IDR",
  });

const DetailTransaction = () => {
  const navigate = useNavigate();
  const { invoiceUid } = useParams();
  const [searchParams] = useSearchParams();
  const [errorMessage, setErrorMessage] = useState("");
  const [printInv, setPrintInv] = useState(null);
  const [openPrintFaktur, setOpenPrintFaktur] = useState(false);
  const [printFakturUrl, setPrintFakturUrl] = useState(null);
  const printFrameRef = useRef(null);

  const listTransactionPath = `/purchase-pbf/list-transaction/${
    searchParams.get("from") ?? 0
  }`;

  const handleBack = () => {
    navigate(listTransactionPath);
  };

  const handleClosePrintFaktur = () => {
    setOpenPrintFaktur(false);
    if (printFakturUrl) {
      URL.revokeObjectURL(printFakturUrl);
      setPrintFakturUrl(null);
    }
  };

  const handlePrintFakturPdf = () => {
    const frame = printFrameRef.current;
    if (frame?.contentWindow) {
      frame.contentWindow.focus();
      frame.contentWindow.print();
    }
  };

  const {
    mutate: mutateTransactionDetail,
    data: dataTransactionDetail,
    isLoading: isLoadingTransactionDetail,
  } = useMutation({
    mutationFn: fetchTransactionDetailNewPbf,
    onError: (err) =>
      setErrorMessage(err.message?.id ? err.message.id : err.message),
  });

  const { mutate: mutateSP, isLoading: isLoadingSP } = useMutation({
    mutationFn: getSpPrintPbf,
    onSuccess: (data) => {
      if (!data) return;

      const url = window.URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SP OLIN ${printInv}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      setPrintInv(null);
    },
    onError: (err) =>
      setErrorMessage(err.message?.id ? err.message.id : err.message),
  });

  const { mutate: mutatePrintFaktur, isLoading: isLoadingPrintFaktur } =
    useMutation({
      mutationFn: getPrintFakturPbf,
      onSuccess: (data) => {
        if (!data) return;

        const pdfBlob =
          data instanceof Blob
            ? data
            : new Blob([data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(pdfBlob);
        setPrintFakturUrl(url);
        setOpenPrintFaktur(true);
      },
      onError: (err) =>
        setErrorMessage(
          err.message?.id
            ? err.message.id
            : typeof err.message === "string"
              ? err.message
              : "Gagal mengunduh faktur",
        ),
    });

  useEffect(() => {
    if (invoiceUid) mutateTransactionDetail({ invoice_uid: invoiceUid });
  }, [invoiceUid]);

  const shipment = dataTransactionDetail?.shipment;
  const trackingHistory = dataTransactionDetail?.tracking_history;
  const dataDetailTransaction =
    dataTransactionDetail?.header?.status_label;
  const shippingInfo = [
    ["Kurir", shipment?.courier_name],
    ["Nomor Pelacakan", shipment?.tracking_number],
    ["Estimasi Tiba", shipment?.estimation],
  ].filter(([, value]) => value && value !== "-");

  const getDetailInvoiceUid = () =>
    dataTransactionDetail?.header?.invoice_uid ||
    dataTransactionDetail?.invoice_uid ||
    invoiceUid;

  const canPrintFaktur = () => {
    const statusValue = Number(
      dataTransactionDetail?.status ??
        dataTransactionDetail?.header?.status ??
        dataTransactionDetail?.status_int ??
        dataTransactionDetail?.shipment?.status,
    );
    const statusLabel = (
      dataTransactionDetail?.status_label ||
      dataTransactionDetail?.header?.status_label ||
      ""
    ).toLowerCase();

    return (
      [2, 4, 5, 6, 7].includes(statusValue)
    );
  };

  const renderProductRow = (product) => (
    <div
      key={product.uuid || product.name}
      className="flex justify-between items-end mb-3"
    >
      <div className="flex items-stretch gap-2">
        <img
          src={product.image}
          alt={product.name}
          className="w-[80px] h-[80px] object-contain rounded-lg border-2"
        />
        <div className="w-full">
          {product.label ? (
            <p
              className={`py-1 px-3 mb-2 rounded-full w-fit truncate
                                  ${
                                    product.label === "Alat Kesehatan"
                                      ? "bg-[#F9EBFE] text-[#AA31F6]"
                                      : product.label ===
                                          "Obat Obat Tertentu (OOT)"
                                        ? "bg-[#FFE7E7] text-[#C62828]"
                                        : product.label === "Obat Prekursor"
                                          ? "bg-[#FFE4E4] text-[#FF0000]"
                                          : product.label ===
                                              "Obat Psikotropika"
                                            ? "bg-[#FEEBF7] text-[#F6317D]"
                                            : "bg-[#EBF5FE] text-[#3194F6]"
                                  }`}
            >
              {product.label}
            </p>
          ) : null}
          <p className="text-[16px] mb-2">{product.name}</p>
          <div className="flex gap-2 items-end">
            {product.discount_amount > 0 && product.discount_value > 0 ? (
              <div className="flex gap-2 items-end">
                <p className="text-gray-500">
                  <s>{toIDR(product.unit_price)}</s>
                </p>
                <p className="bg-red-100 text-red-500 p-1 rounded-sm font-semibold">
                  {formatPercent(product.discount_value)}
                </p>
              </div>
            ) : null}
            <p>
              {toIDR(
                (product.unit_price * (100 - (product.discount_value || 0))) /
                  100,
              )}{" "}
              x {product.qty}
            </p>
          </div>
        </div>
      </div>
      <div className="text-[16px] font-semibold">{toIDR(product.line_net)}</div>
    </div>
  );

  return (
    <div className="text-[12px] text-[#2C3E5D]">
      <PurchaseHeader />
      <div className="mt-[70px]">
        <div className="px-3 pt-2 mb-4">
          <BreadcrumbPurchase />
        </div>
        <div className="px-3 mb-4 flex items-center gap-3">
          <button
            type="button"
            onClick={handleBack}
            className="hover:text-gray-400"
          >
            <FaChevronLeft className="text-[18px]" />
          </button>
          <p className="text-[24px] font-semibold">Detail Pesanan</p>
        </div>

        <div className="px-3 mb-4">
          <div className="w-full bg-white rounded-lg p-4 md:p-8 h-fit">
            {dataTransactionDetail && !isLoadingTransactionDetail ? (
              <div className="w-full">
                <div className="flex flex-col md:flex-row gap-4 mb-4 items-start">
                  <div className="w-full">
                    <p>Distributor: PT. Digital Pharma Andalan Indonesia</p>
                    <div>
                      <p>
                        Tanggal Transaksi:{" "}
                        {moment(dataTransactionDetail.order_date).format(
                          "DD MMMM YYYY",
                        )}
                      </p>
                      <p>
                        Nomor Invoice: {dataTransactionDetail.header.invoice_no}
                      </p>
                      <p>
                        Pelanggan: {dataTransactionDetail.header.company_name}
                      </p>
                    </div>
                  </div>

                  {dataTransactionDetail.status_int !== 10 ||
                  canPrintFaktur() ? (
                    <div className="w-full flex flex-col sm:flex-row gap-2 self-start">
                      {dataTransactionDetail.status_int !== 10 ? (
                        <button
                          onClick={() => {
                            setPrintInv(
                              dataTransactionDetail.header?.invoice_no,
                            );
                            setTimeout(() => {
                              mutateSP({
                                invNum: invoiceUid,
                              });
                            }, 500);
                          }}
                          className="border-2 hover:bg-gray-100 rounded-md w-full h-fit py-2 px-3 disabled:bg-gray-300 disabled:text-white"
                          disabled={isLoadingSP}
                        >
                          {isLoadingSP ? (
                            <CircularProgress size={20} />
                          ) : (
                            <div className="flex items-center justify-center gap-2">
                              <LuDownload />
                              Unduh SP
                            </div>
                          )}
                        </button>
                      ) : null}
                      {canPrintFaktur() ? (
                        <button
                          onClick={() => {
                            mutatePrintFaktur({
                              invoice_uid: getDetailInvoiceUid(),
                            });
                          }}
                          className="border-2 hover:bg-gray-100 rounded-md w-full h-fit py-2 px-3 disabled:bg-gray-300 disabled:text-white"
                          disabled={isLoadingPrintFaktur || !getDetailInvoiceUid()}
                        >
                          {isLoadingPrintFaktur ? (
                            <CircularProgress size={20} />
                          ) : (
                            <div className="flex items-center justify-center gap-2">
                              <LuPrinter />
                              Print Faktur
                            </div>
                          )}
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>

                <div className="mb-4">
                  <p className="text-[16px] font-semibold mb-2">
                    Rincian Pesanan
                  </p>

                  <div className="flex justify-between items-center text-[16px]">
                    <p>Produk</p>
                    <p>Subtotal</p>
                  </div>
                  <hr className="my-2" />
                  <>
                    {dataTransactionDetail.items?.filter(
                      (product) =>
                        !SPECIAL_LABEL.map(
                          (label) => label.label_name,
                        ).includes(product.label),
                    ).length > 0 ? (
                      <p className="text-[16px] mb-4">General</p>
                    ) : null}
                    {dataTransactionDetail.items
                      ?.filter(
                        (product) =>
                          !SPECIAL_LABEL.map(
                            (label) => label.label_name,
                          ).includes(product.label),
                      )
                      .map((product) => renderProductRow(product))}

                    {SPECIAL_LABEL.map((label) => (
                      <div key={label.label_name}>
                        {dataTransactionDetail.items?.filter(
                          (product) => product.label === label.label_name,
                        ).length > 0 ? (
                          <>
                            {dataTransactionDetail.items?.filter(
                              (product) =>
                                !SPECIAL_LABEL.map(
                                  (item) => item.label_name,
                                ).includes(product.label),
                            ).length === 0 ? null : (
                              <hr className="my-3" />
                            )}
                            <p className="text-[16px] mb-4">
                              {label.label_name}
                            </p>
                          </>
                        ) : null}
                        {dataTransactionDetail.items
                          ?.filter(
                            (product) => product.label === label.label_name,
                          )
                          .map((product) => renderProductRow(product))}
                      </div>
                    ))}
                  </>
                </div>

                <p className="text-[16px] font-semibold mb-2">
                  Rincian Pembayaran
                </p>

                <div className="flex justify-between items-center text-[16px]">
                  <p>Produk</p>
                  <p>Subtotal</p>
                </div>
                <hr className="my-2" />
                <div className="flex justify-between items-center mb-2">
                  <p>Metode Pembayaran</p>
                  <p>{dataTransactionDetail.header.payment_method_name}</p>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <p>
                    Total harga ({dataTransactionDetail.items.length} produk)
                  </p>
                  <p>{toIDR(dataTransactionDetail.payment_summary.subtotal)}</p>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <p>Biaya pengiriman</p>
                  <p>
                    {dataTransactionDetail.payment_summary.shipping_fee &&
                    dataTransactionDetail.payment_summary.shipping_fee > 0
                      ? toIDR(
                          dataTransactionDetail.payment_summary.shipping_fee,
                        )
                      : "Gratis"}
                  </p>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <p>Pajak (PPN 11%)</p>
                  <p>
                    {toIDR(dataTransactionDetail.payment_summary.ppn_amount)}
                  </p>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <p>Biaya admin</p>
                  <p>
                    {toIDR(dataTransactionDetail.payment_summary.payment_fee)}
                  </p>
                </div>
                {dataTransactionDetail.payment_summary.discount_shipping > 0 ? (
                  <div className="flex justify-between items-center mb-2">
                    <p>Promo</p>
                    <p>
                      -
                      {toIDR(
                        dataTransactionDetail.payment_summary.discount_shipping,
                      )}
                    </p>
                  </div>
                ) : null}
                {dataTransactionDetail.payment_summary.loyalty_point_used >
                0 ? (
                  <div className="flex justify-between items-center mb-2">
                    <p>Olin Point</p>
                    <p>
                      -
                      {toIDR(
                        dataTransactionDetail.payment_summary
                          .loyalty_point_used,
                      )}
                    </p>
                  </div>
                ) : null}
                <hr className="mb-2" />
                <div className="flex justify-between items-center mb-4 font-semibold">
                  <p>Total</p>
                  <p>
                    {toIDR(dataTransactionDetail.payment_summary.grand_total)}
                  </p>
                </div>

                {shippingInfo.length > 0 || shipment?.tracking_url ? (
                  <div className="mb-4">
                    <p className="text-[16px] font-semibold mb-2">
                      Informasi Pengiriman
                    </p>
                    {shippingInfo.map(([label, value]) => (
                      <div
                        key={label}
                        className="flex justify-between items-center mb-2"
                      >
                        <p>{label}</p>
                        <p className="font-semibold">{value}</p>
                      </div>
                    ))}
                    {shipment?.tracking_url ? (
                      <a
                        href={shipment.tracking_url}
                        target="_blank"
                        rel="noreferrer"
                        className="underline text-[#94C679]"
                      >
                        Lacak Pengiriman
                      </a>
                    ) : null}
                  </div>
                ) : null}

                {trackingHistory?.length ? (
                  <div>
                    <div className="flex gap-4 justify-between items-center mb-4">
                      <p className="text-[16px] font-semibold mb-2">
                        Status Pesanan
                      </p>
                      <p className="bg-gray-100 py-2 px-4 rounded-full">
                        {dataDetailTransaction}
                      </p>
                    </div>

                    {trackingHistory.map((item, i) => (
                      <div key={i} className="flex gap-4">
                        <div className="relative w-6 flex justify-center shrink-0">
                          {i !== trackingHistory.length - 1 && (
                            <div className="absolute top-6 bottom-0 left-[11px] border-l-2" />
                          )}

                          {i === 0 ? (
                            <div className="relative z-10 rounded-full w-[24px] h-[24px] border-3 border-[#2C3E5D] shrink-0 p-[4px]">
                              <FaCircle className="w-full h-full" />
                            </div>
                          ) : (
                            <FaCheckCircle className="relative z-10 text-[32px] text-[#2C3E5D]" />
                          )}
                        </div>
                        <div className="w-full pb-4">
                          <p>{item.message}</p>
                          <div className="flex gap-2 items-center flex-wrap text-gray-400">
                            {moment(item.event_time).format("DD/MM/YYYY")}
                            <FaCircle className="text-[3px]" />
                            {moment(item.event_time).format("HH:mm")}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}

                <div className="flex justify-end items-center gap-2">
                  <TransactionActions
                    invoice={dataTransactionDetail.header}
                    onChanged={() =>
                      mutateTransactionDetail({ invoice_uid: invoiceUid })
                    }
                    onError={setErrorMessage}
                  />
                  <button
                    type="button"
                    onClick={handleBack}
                    className="text-white bg-[#94C679] hover:bg-gray-300 rounded-md w-fit py-2 px-4 text-[14px] disabled:bg-gray-300"
                  >
                    Kembali
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center text-red-500 py-8">
                {isLoadingTransactionDetail ? (
                  <CircularProgress size={20} />
                ) : (
                  <p>{errorMessage !== "" ? errorMessage : "Tidak ada data"}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <Modal open={openPrintFaktur} onClose={handleClosePrintFaktur}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "80%",
            maxWidth: 1000,
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            p: 0,
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
          }}
          className="text-left text-[12px] text-[#2C3E5D]"
        >
          <div className="flex justify-between items-center px-6 py-3 border-b">
            <p className="text-[16px] font-semibold">Print Faktur</p>
            <RxCross1
              className="cursor-pointer hover:text-red-500"
              onClick={handleClosePrintFaktur}
            />
          </div>
          <div className="px-6 py-3 h-[65vh]">
            {printFakturUrl ? (
              <iframe
                ref={printFrameRef}
                title="Print Faktur"
                src={printFakturUrl}
                className="w-full h-full border rounded-md"
              />
            ) : null}
          </div>
          <div className="flex justify-end gap-2 px-6 py-3 border-t">
            <button
              type="button"
              onClick={handleClosePrintFaktur}
              className="border-2 hover:bg-gray-100 rounded-md py-2 px-4"
            >
              Tutup
            </button>
            <button
              type="button"
              onClick={handlePrintFakturPdf}
              className="text-white bg-[#94C679] hover:bg-gray-300 rounded-md py-2 px-4"
            >
              Print
            </button>
          </div>
        </Box>
      </Modal>
      <ErrorPopup
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
      />
    </div>
  );
};

export default DetailTransaction;
